import React, { createContext, useContext, useState, useEffect } from 'react';
import { databases, DATABASE_ID, USERS_COLLECTION_ID, Query, User } from '../config/appwrite';
import { useNavigate } from 'react-router-dom';
import useRealtimeSession from '../hooks/useRealtimeSession';
import { usePresenceHeartbeat } from '../hooks/usePresenceHeartbeat';
import DBConstants from '../constants/db.constants';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (username: string, password: string) => Promise<User>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Use real-time session monitoring (replaces polling)
  useRealtimeSession(user?.id);

  // Use the presence heartbeat hook (60 second intervals)
  const { stopHeartbeat } = usePresenceHeartbeat(
    !!user, // isAuthenticated
    user?.id, // User ID
    60000 // 60 seconds
  );

  useEffect(() => {
    const clearAuth = () => {
      sessionStorage.removeItem('sessionToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      localStorage.removeItem('activeEventId');
      setUser(null);
      setLoading(false);
    };

    const initializeAuth = async () => {
      const sessionToken = sessionStorage.getItem('sessionToken');
      const storedUser = localStorage.getItem('user');

      if (!sessionToken || !storedUser) {
        clearAuth();
        return;
      }

      let parsedUser: any;
      try {
        parsedUser = JSON.parse(storedUser);
      } catch {
        clearAuth();
        return;
      }

      // Verify session is still valid in DB
      try {
        const userDoc = await databases.getDocument(DATABASE_ID, USERS_COLLECTION_ID, parsedUser.id);
        if ((userDoc as any).last_logout_tab !== sessionToken || (userDoc as any).is_logged_in !== 'true') {
          clearAuth();
          return;
        }
      } catch {
        // If DB check fails, fall through and let realtime/heartbeat handle it
      }

      setUser(parsedUser);
      setLoading(false);
    };

    initializeAuth();

    // Handle tab/browser close - synchronous approach
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const currentUser = localStorage.getItem('user');
      const sessionToken = sessionStorage.getItem('sessionToken');

      if (currentUser && sessionToken) {
        try {
          const parsedUser = JSON.parse(currentUser);

          // Use synchronous XMLHttpRequest (only reliable way during unload)
          const xhr = new XMLHttpRequest();
          xhr.open(
            'PATCH',
            `${process.env.REACT_APP_APPWRITE_ENDPOINT}/databases/${DATABASE_ID}/collections/${USERS_COLLECTION_ID}/documents/${parsedUser.id}`,
            false
          ); // false = synchronous
          xhr.setRequestHeader('Content-Type', 'application/json');
          xhr.setRequestHeader(
            'X-Appwrite-Project',
            process.env.REACT_APP_APPWRITE_PROJECT_ID || ''
          );
          xhr.send(
            JSON.stringify({
              is_logged_in: 'false',
              last_logout_tab: null,
            })
          );
        } catch (error) {
          console.error('Error in beforeunload handler:', error);
        }
      }
    };

    // Handle page hide (more reliable on mobile)
    const handlePageHide = (e: PageTransitionEvent) => {
      const currentUser = localStorage.getItem('user');
      const sessionToken = sessionStorage.getItem('sessionToken');

      if (currentUser && sessionToken) {
        try {
          const parsedUser = JSON.parse(currentUser);

          // Use navigator.sendBeacon for pagehide (non-blocking)
          const data = new Blob(
            [
              JSON.stringify({
                is_logged_in: 'false',
                last_logout_tab: null,
              }),
            ],
            { type: 'application/json' }
          );

          navigator.sendBeacon(
            `${process.env.REACT_APP_APPWRITE_ENDPOINT}/databases/${DATABASE_ID}/collections/${USERS_COLLECTION_ID}/documents/${parsedUser.id}`,
            data
          );
        } catch (error) {
          console.error('Error in pagehide handler:', error);
        }
      }
    };

    // Handle visibility change (when tab is hidden)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        const currentUser = localStorage.getItem('user');
        const sessionToken = sessionStorage.getItem('sessionToken');

        if (currentUser && sessionToken) {
          try {
            const parsedUser = JSON.parse(currentUser);

            // Use fetch with keepalive
            fetch(
              `${process.env.REACT_APP_APPWRITE_ENDPOINT}/databases/${DATABASE_ID}/collections/${USERS_COLLECTION_ID}/documents/${parsedUser.id}`,
              {
                method: 'PATCH',
                headers: {
                  'Content-Type': 'application/json',
                  'X-Appwrite-Project': process.env.REACT_APP_APPWRITE_PROJECT_ID || '',
                },
                body: JSON.stringify({
                  is_logged_in: 'false',
                  last_logout_tab: null,
                }),
                keepalive: true,
              }
            ).catch(err => {
              console.error('Error logging out on visibility change:', err);
            });
          } catch (error) {
            console.error('Error in visibilitychange handler:', error);
          }
        }
      }
    };

    // Heartbeat to keep session alive and detect changes
    const heartbeatInterval = setInterval(async () => {
      const currentUser = localStorage.getItem('user');
      const sessionToken = sessionStorage.getItem('sessionToken');

      if (currentUser && sessionToken) {
        try {
          const parsedUser = JSON.parse(currentUser);

          // Check if session is still valid
          const userDoc = await databases.getDocument(
            DATABASE_ID,
            USERS_COLLECTION_ID,
            parsedUser.id
          );

          // Logout if session token changed or admin force-logged out
          const doc = userDoc as any;
          if (doc.last_logout_tab !== sessionToken || doc.is_logged_in !== 'true') {
            console.log('Session invalidated, logging out...');
            sessionStorage.removeItem('sessionToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            localStorage.removeItem('activeEventId');
            setUser(null);
            window.location.href = '/login';
          }
        } catch (error) {
          console.error('Heartbeat error:', error);
        }
      }
    }, 10000); // Every 10 seconds

    // Listen for storage changes (logout in other tabs)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'user' && !e.newValue) {
        // User was cleared in another tab
        console.log('User logged out in another tab');
        sessionStorage.removeItem('sessionToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('activeEventId');
        setUser(null);
        window.location.href = '/login';
      }
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('storage', handleStorageChange);

    // Cleanup function
    return () => {
      clearInterval(heartbeatInterval);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const signIn = async (username: string, password: string): Promise<User> => {
    setLoading(true);
    console.log('Starting sign in process for user:', username);

    try {
      // Query user by username and password
      const response = await databases.listDocuments(DATABASE_ID, USERS_COLLECTION_ID, [
        Query.equal('username', username),
        Query.equal('password', password),
        Query.limit(1),
      ]);

      if (!response.documents || response.documents.length === 0) {
        console.error('Sign in error: Invalid credentials');
        throw new Error('Invalid credentials');
      }

      const doc = response.documents[0];

      // Map Appwrite document to User type
      const data: User = {
        id: doc.$id,
        username: doc.username as string,
        password: doc.password as string,
        role: doc.role as 'admin' | 'user' | 'superadmin',
        is_blocked: doc.is_blocked as boolean,
        is_logged_in: doc.is_logged_in as string,
        last_logout_tab: doc.last_logout_tab as string,
        name: doc.name as string,
        jamiaat: doc.jamiaat as string,
        created_at: doc.created_at as string,
      };

      if (data.is_logged_in === 'true') {
        console.log('User already logged in on another device');
        throw new Error('This account is already logged in on another device');
      }

      // Generate new session token
      const sessionToken = Math.random().toString(36).substring(2) + Date.now().toString(36);

      // Store in both sessionStorage and localStorage
      sessionStorage.setItem('sessionToken', sessionToken);
      localStorage.setItem('refreshToken', sessionToken); // This will persist through refreshes

      // Update user with new session token and logged in status
      try {
        await databases.updateDocument(DATABASE_ID, USERS_COLLECTION_ID, data.id, {
          is_logged_in: 'true',
          last_logout_tab: sessionToken,
        });
      } catch (updateError) {
        console.error('Error updating login status:', updateError);
        throw updateError;
      }

      const userData = data;
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      setLoading(false);
      return userData;
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const signOut = async () => {
    if (user) {
      const sessionToken = sessionStorage.getItem('sessionToken');

      // Stop the heartbeat immediately
      stopHeartbeat();

      // Update logout status and clear session token
      try {
        // First check if the session token matches
        const userDoc = await databases.getDocument(DATABASE_ID, USERS_COLLECTION_ID, user.id);

        if ((userDoc as unknown as User).last_logout_tab === sessionToken) {
          await databases.updateDocument(DATABASE_ID, USERS_COLLECTION_ID, user.id, {
            is_logged_in: 'false',
            last_logout_tab: null,
          });
        }
      } catch (error) {
        console.error('Error updating logout status:', error);
      }

      // Clear both tokens
      sessionStorage.removeItem('sessionToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
